"use client";
import { Forecastday, Hour } from "@/interfaces/IweatherMarine";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid2,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import moment from "moment";
import Image from "next/image";
import SunsetIcon from "@mui/icons-material/WbTwilightRounded";
import MoonriseIcon from "@mui/icons-material/NightsStayRounded";
import MoonsetIcon from "@mui/icons-material/DarkModeRounded";
import WindIcon from "@mui/icons-material/AirRounded";
import FeelsLikeIcon from "@mui/icons-material/ThermostatRounded";
import HumidityIcon from "@mui/icons-material/OpacityRounded";
import CloseIcon from "@mui/icons-material/Close";
import PrevIcon from "@mui/icons-material/ChevronLeftRounded";
import NextIcon from "@mui/icons-material/ChevronRightRounded";
import LocationIcon from "@mui/icons-material/LocationOn";
import TsunamiIcon from "@mui/icons-material/Tsunami";
import CloudCircleIcon from "@mui/icons-material/CloudCircle";

export const WeatherForcastDayCard: React.FC<{
  forecastday: Forecastday;
  onClick: () => void;
}> = ({ onClick, forecastday }) => {
  const formatTime = (timeString: string) => {
    const parts = timeString.split(" ");
    return parts[1];
  };
  return forecastday !== null ? (
    <Box
      sx={{
        maxWidth: { xs: "100%", md: "152px" },
        width: { xs: "100%", md: "152px" },
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          mb: "8px",
          ml: "-8px",
          fontWeight: 300,
          fontSize: "14px",
          textAlign: "center",
        }}
      >
        {moment(forecastday?.date).format("dddd, Do MMM")}
      </Box>
      <Box
        sx={{
          p: "12px 5px",
          width: "100%",
          borderRadius: "12px",
          boxSizing: "border-box",
          backgroundColor: "rgb(249,255,210)",
          backgroundImage:
            "linear-gradient(130deg, rgb(215 213 255) 0%, #d1ceff 35%, #b2dbff 100%)",
          boxShadow: "4px 4px 15px rgb(0 0 0 / 20%)",
          border: "1px solid rgb(140, 138, 184)",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Image
            src={forecastday?.day?.condition?.icon || ""}
            alt={forecastday?.day?.condition?.text || "Weather condition"}
            width={60}
            height={60}
            unoptimized
          />
          <Box
            sx={{
              fontWeight: 400,
              fontSize: "14px",
              textTransform: "uppercase",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box>
              H : {forecastday?.day?.maxtempC || ""} <span>°C</span>
            </Box>

            <Box>
              L : {forecastday?.day?.mintempC || ""} <span>°C</span>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            fontWeight: 600,
            fontSize: "2rem",
            span: {
              top: "-5px",
              position: "relative",
              fontSize: "1.5rem",
              fontWeight: "900",
            },
          }}
        >
          {forecastday?.day?.avgtempC || ""} <span>°C</span>
        </Box>

        <Box sx={{ fontWeight: 300, alignSelf: "center", fontSize: "14px", mb: "8px" }}>
          {forecastday?.day?.condition?.text == ""
            ? "Partly Cloudy"
            : forecastday?.day?.condition?.text}
        </Box>

        <Box
          sx={{
            p: 1,
            bgcolor: "#eeefff",
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: "8px",
              gap: "8px",
            }}
          >
            <TsunamiIcon /> Tide info
          </Box>

          {forecastday?.day?.tides?.[0]?.tide &&
            forecastday.day.tides[0]?.tide.map((tideItem, index) => (
              <Box key={index} sx={{ mb: 0.5 }}>
                <Box sx={{ display: "flex" }}>
                  <Typography variant="body2" sx={{ alignSelf: "center" }}>
                    {" "}
                    Time at {formatTime(tideItem?.tideTime || "")}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      mr: 1,
                      bgcolor: tideItem.tideType === "HIGH" ? "#ff0000" : "primary.main",
                    }}
                  />
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography variant="body1" sx={{ mr: 1 }}>
                      {tideItem.tideHeightMt}m
                    </Typography>
                    <Box
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 0.5,
                        fontSize: "0.75rem",
                        bgcolor: tideItem.tideType === "HIGH" ? "#fff3f3" : "primary.light",
                        color: tideItem.tideType === "HIGH" ? "#ff0000" : "primary.dark",
                      }}
                    >
                      {tideItem.tideType}
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
        </Box>
      </Box>
    </Box>
  ) : (
    <></>
  );
};

export const WeatherForcastDayModal: React.FC<{
  open: boolean;
  handleClose: () => void;
  dayWeather: Forecastday | null;
  locationData: string | undefined;
  nextHandler: () => void;
  prevHandler: () => void;
  isNextDisable: boolean;
  isPrevDisable: boolean;
}> = ({
  open,
  handleClose,
  dayWeather,
  locationData,
  nextHandler,
  prevHandler,
  isNextDisable,
  isPrevDisable,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullScreen={fullScreen}
      fullWidth
      sx={{
        ".MuiPaper-root": {
          borderRadius: "25px",
        },
        ".MuiBackdrop-root": {
          backdropFilter: "blur(5px)",
        },
      }}
    >
      {dayWeather ? (
        <DialogContent sx={{ px: "18px" }}>
          <Box
            sx={{
              bgcolor: "",
              display: { xs: "none", sm: "flex", md: "none" },
              justifyContent: "space-between",
            }}
          >
            {/* Right side summary-  sm */}
            <Typography
              sx={{
                display: { xs: "none", sm: "flex", md: "none" },
                fontSize: { xs: "20px", sm: "1.4rem", md: "1.4rem" },
                fontWeight: 600,
                lineHeight: 1.4,
              }}
            >
              Future Weather
              <Box
                component={"span"}
                sx={{
                  fontWeight: 300,
                  fontSize: { xs: "19px", sm: "1.4rem", md: "1.4rem" },
                  pt: { xs: "2px", sm: "0px", md: "0px" },
                  pl: { xs: "10px", sm: "10px", md: "0px" },
                  display: { xs: "none", sm: "inline-block", md: "none" },
                }}
              >
                (On {moment(dayWeather.date).format("dddd, Do MMM")})
              </Box>
            </Typography>
            <Button
              sx={{
                minWidth: "unset",
                bgcolor: "#ccc",
                color: "#000",
                p: "7px",
                borderRadius: "10px",
                border: "1px solid #aaa",
              }}
              onClick={handleClose}
              aria-label="Close"
            >
              <CloseIcon />
            </Button>
          </Box>
          <Box
            sx={{
              display: { xs: "none", sm: "flex", md: "none" },
              alignItems: "center",
              gap: "2px",
              fontWeight: 500,
              fontSize: "15px",
              ml: "-3px",
              mt: "-7px",
              color: "#666",
              mb: { sm: "1rem", md: "0px" },
              // py: '1rem',
            }}
          >
            <LocationIcon sx={{ width: "22px" }} />
            {locationData || ""}
          </Box>
          <Box sx={{ display: "flex", gap: "1.2rem" }}>
            {/* Weather  Day Summary Card */}
            <Box
              sx={{
                p: "1rem",
                borderRadius: "15px",
                border: "1px solid #060273",
                backgroundColor: "rgb(210, 239, 255)",
                backgroundImage:
                  "linear-gradient(130deg, rgb(179 136 255) 0%, rgb(167 184 255) 35%, rgb(162 225 247) 100%)",
                boxShadow: "4px 4px 15px rgb(0 0 0 / 0%)",
                minWidth: "240px",
                display: { xs: "none", sm: "block", md: "block" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "20px",
                }}
              >
                <Box sx={{ mt: "-8px", ml: "-5px" }}>
                  <Image
                    src={dayWeather?.day?.condition?.icon || ""}
                    alt={dayWeather?.day?.condition?.text || "Weather condition"}
                    width={70}
                    height={70}
                    objectFit="cover"
                    unoptimized
                  />
                </Box>

                <Box sx={{ textAlign: "right" }}>
                  <Box sx={{ fontWeight: 400, mt: "-8px", color: "#333" }}>
                    {dayWeather?.day?.condition?.text == ""
                      ? "Partly Cloudy"
                      : dayWeather?.day?.condition?.text || ""}
                  </Box>

                  <Box
                    sx={{
                      mt: "-4px",
                      fontWeight: 700,
                      fontSize: "2.6rem",
                      span: {
                        top: "-13px",
                        position: "relative",
                        fontSize: "1.5rem",
                        fontWeight: 900,
                      },
                    }}
                  >
                    {dayWeather?.day?.avgtempC || ""} <span>°C</span>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  fontSize: "13px",
                  fontWeight: 400,
                  mb: "-6px",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                  color: "#555",
                  span: {
                    color: "#000",
                    fontWeight: 700,
                  },
                }}
              >
                <Box>
                  HIGH : <span>{dayWeather?.day?.maxtempC || ""} °C</span>
                </Box>
                <Box>
                  LOW :<span> {dayWeather?.day?.mintempC || ""} °C</span>
                </Box>
              </Box>
            </Box>

            {/* Right side summary */}
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  display: { xs: "flex", sm: "none", md: "flex" },

                  justifyContent: "space-between",
                }}
              >
                {/* Weather Forecast - mobile*/}
                <Box sx={{}}>
                  <Typography
                    sx={{
                      display: { xs: "flex", sm: "flex", md: "flex" },
                      fontSize: { xs: "25px", sm: "1.4rem", md: "1.4rem" },
                      fontWeight: 600,
                      lineHeight: 1.4,
                    }}
                  >
                    Future Weather
                    <br />
                    <Box
                      component={"span"}
                      sx={{
                        fontWeight: 300,
                        fontSize: { xs: "19px", sm: "1.4rem", md: "1.4rem" },
                        pt: { xs: "2px", sm: "0px", md: "0px" },
                        display: { xs: "none", sm: "none", md: "flex" },
                        pl: { xs: "10px", sm: "0px", md: "0px" },
                      }}
                    >
                      {" "}
                      (On {moment(dayWeather?.date).format("dddd, Do MMM")})
                    </Box>
                  </Typography>
                </Box>
                <Button
                  sx={{
                    minWidth: "unset",
                    width: { xs: "35px", sm: "unset", md: "unset" },
                    height: { xs: "35px", sm: "unset", md: "unset" },
                    bgcolor: "#ccc",
                    color: "#000",
                    p: "7px",
                    borderRadius: "10px",
                    border: "1px solid #aaa",
                  }}
                  onClick={handleClose}
                  aria-label="Close"
                >
                  <CloseIcon />
                </Button>
              </Box>
              <Typography
                sx={{
                  fontWeight: 300,
                  fontSize: { xs: "18px", sm: "1.4rem", md: "1.4rem" },
                  pt: { xs: "0px", sm: "0px", md: "0px" },
                  display: { xs: "flex", sm: "none", md: "none" },
                  position: "relative",
                  top: "-7px",
                }}
              >
                {" "}
                (On {moment(dayWeather?.date).format("dddd, Do MMM")})
              </Typography>
              <Box
                sx={{
                  display: { xs: "flex", sm: "none", md: "flex" },
                  alignItems: "center",
                  gap: "2px",
                  fontWeight: 500,
                  fontSize: "15px",
                  ml: "-3px",
                  mt: "-4px",
                  color: "#666",
                  pt: "0px",
                  mb: "1rem",
                }}
              >
                <LocationIcon sx={{ width: "22px" }} />
                {locationData}
                {/* {locationData?.name}, {locationData?.country} */}
              </Box>

              <Box
                sx={{
                  display: { xs: "none", sm: "flex", md: "flex" },
                  gap: "1.2rem",
                  mt: { sm: "10px", md: "18px" },
                  flexWrap: "wrap",
                }}
              >
                <WeatherAstroWidget
                  icon={<SunsetIcon />}
                  title="Sunrise"
                  value={dayWeather.astro?.sunrise}
                />

                <WeatherAstroWidget
                  icon={<SunsetIcon />}
                  title="Sunset"
                  value={dayWeather.astro?.sunset}
                />

                <WeatherAstroWidget
                  icon={<MoonriseIcon />}
                  title="Moonrise"
                  value={dayWeather?.astro?.moonrise}
                />

                <WeatherAstroWidget
                  icon={<MoonsetIcon />}
                  title="Moonset"
                  value={dayWeather?.astro?.moonset}
                />

                <WeatherAstroWidget
                  icon={<WindIcon />}
                  title="Max Wind"
                  value={`${dayWeather?.day?.maxwindKph} km/h`}
                />
              </Box>
            </Box>
          </Box>
          {/* Weather  Day Summary Card - sm */}
          <Box sx={{ display: { xs: "flex", sm: "none", md: "none" } }}>
            <Box
              sx={{
                p: "1rem",
                borderRadius: "15px",
                border: "1px solid #FFE600",
                backgroundColor: "rgb(249,255,210)",
                backgroundImage:
                  "linear-gradient(130deg, rgba(252,255,233,1) 0%, rgba(247,254,200,1) 35%, rgba(247,234,162,1) 100%)",
                boxShadow: "4px 4px 15px rgb(0 0 0 / 0%)",
                minWidth: "240px",
                display: { xs: "block", sm: "none", md: "none" },
                width: "fit-content",
                // ml: '4.2rem',
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "20px",
                }}
              >
                <Box sx={{ mt: "-8px", ml: "-5px" }}>
                  <Image
                    src={dayWeather?.day?.condition?.icon || ""}
                    alt={dayWeather?.day?.condition?.text || "Weather condition"}
                    width={70}
                    height={70}
                    objectFit="cover"
                    unoptimized
                  />
                </Box>

                <Box sx={{ textAlign: "right" }}>
                  <Box sx={{ fontWeight: 400, mt: "-8px", color: "#333" }}>
                    {dayWeather?.day?.condition?.text == ""
                      ? "Partly Cloudy"
                      : dayWeather?.day?.condition?.text}
                  </Box>

                  <Box
                    sx={{
                      mt: "-4px",
                      fontWeight: 700,
                      fontSize: "2.6rem",
                      span: {
                        top: "-13px",
                        position: "relative",
                        fontSize: "1.5rem",
                        fontWeight: 900,
                      },
                    }}
                  >
                    {dayWeather?.day?.avgtempC} <span>°C</span>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  fontSize: "13px",
                  fontWeight: 400,
                  mb: "-6px",
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px",
                  color: "#555",
                  span: {
                    color: "#000",
                    fontWeight: 700,
                  },
                }}
              >
                <Box>
                  HIGH : <span>{dayWeather?.day?.maxtempC} °C</span>
                </Box>
                <Box>
                  LOW :<span> {dayWeather?.day?.mintempC} °C</span>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: { xs: "flex", sm: "none", md: "none" },
                justifyContent: "center",
                flexWrap: "wrap",
                gap: "1rem",
              }}
            >
              <WeatherAstroWidget
                icon={<SunsetIcon />}
                title="Sunrise"
                value={dayWeather?.astro?.sunrise}
              />

              <WeatherAstroWidget
                icon={<SunsetIcon />}
                title="Sunset"
                value={dayWeather?.astro?.sunset}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: { xs: "flex", sm: "none", md: "none" },
              flexDirection: "row",
              mt: "12px",
              gap: "12px",
            }}
          >
            <WeatherAstroWidget
              icon={<MoonriseIcon />}
              title="Moonrise"
              value={dayWeather?.astro?.moonrise}
            />

            <WeatherAstroWidget
              icon={<MoonsetIcon />}
              title="Moonset"
              value={dayWeather?.astro?.moonset}
            />

            <WeatherAstroWidget
              icon={<WindIcon />}
              title="Max Wind"
              value={`${dayWeather?.day?.maxwindKph} km/h`}
            />
          </Box>
          <Box sx={{ mt: "1.7rem" }}>
            <Box
              sx={{
                mt: { xs: "3rem", sm: "3rem", md: "0px" },
                position: "relative",
                borderBottom: "1px solid #ddd",
                mb: "1.5rem",
              }}
            >
              <Box
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  position: "absolute",
                  top: "-11px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 10,
                  bgcolor: "#fff",
                  px: "8px",
                }}
              >
                Hourly Forecast
              </Box>
            </Box>

            <Grid2 container spacing={2}>
              {dayWeather?.hour?.map((hourData, hourIndex) => (
                <Grid2 key={hourIndex} size={{ xs: 4, md: 1.5 }}>
                  <WeatherModalHourlyCard hourData={hourData} />
                </Grid2>
              ))}
            </Grid2>
          </Box>
        </DialogContent>
      ) : (
        "loading"
      )}

      <DialogActions sx={{ p: "0 1rem 1rem", justifyContent: "space-between" }}>
        <Button
          onClick={prevHandler}
          disabled={isPrevDisable}
          disableElevation
          variant="contained"
          sx={{ borderRadius: "10px" }}
          startIcon={<PrevIcon />}
        >
          Prev
        </Button>
        <Button
          onClick={nextHandler}
          disabled={isNextDisable}
          disableElevation
          variant="contained"
          sx={{ borderRadius: "10px" }}
          endIcon={<NextIcon />}
        >
          Next
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const WeatherAstroWidget: React.FC<{
  icon: React.ReactNode;
  title: string;
  value?: string;
}> = ({ icon, title, value }) => {
  return (
    <Box sx={{ display: "flex", gap: "9px", alignItems: "center" }}>
      <Box
        sx={{
          p: "8px",
          lineHeight: 0.9,
          borderRadius: "50%",
          bgcolor: "#c4c1f9",
          color: "#060273",
          border: "1px solid #a6a90022",
          display: "grid",
          placeItems: "center",
        }}
      >
        {icon}
      </Box>
      <Box
        sx={{
          fontSize: "13px",
          fontWeight: 400,
          lineHeight: 1.4,
        }}
      >
        <Box sx={{ color: "#666" }}>{title}</Box>
        <Box sx={{ fontSize: "15px", fontWeight: 600 }}>{value}</Box>
      </Box>
    </Box>
  );
};

export const WeatherModalHourlyCard: React.FC<{
  hourData: Hour;
}> = ({ hourData }) => {
  return (
    <>
      <Box
        sx={{
          fontSize: "14px",
          fontWeight: 400,
          color: "#444",
          textAlign: "center",
          mb: "5px",
        }}
      >
        {moment(hourData.time).format("hh:mm a")}
      </Box>
      <Box
        sx={{
          p: "12px 12px 8px",
          bgcolor: "#f7f5ff",
          borderRadius: "18px",
          border: "1px solid #ddd",
          height: { xs: "275px", sm: "240px", md: "285px" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: "4px",
            alignItems: "center",
          }}
        >
          <Box sx={{ ml: "-5px", mt: "-3px" }}>
            <Image
              src={hourData?.condition?.icon || ""}
              alt={hourData?.condition?.text || "Weather condition"}
              width={45}
              height={45}
              unoptimized
            />
          </Box>

          <Box
            sx={{
              color: "#444",
              fontWeight: 600,
              fontSize: "1.3rem",
              span: {
                top: "-12px",
                position: "relative",
                fontSize: "1rem",
                fontWeight: 600,
              },
            }}
          >
            {hourData.tempC}
            <span>°C</span>
          </Box>
        </Box>

        <Box sx={{ fontWeight: 400, fontSize: "14px", m: "6px 0 14px" }}>
          {hourData?.condition?.text == "" ? "Partly Cloudy" : hourData?.condition?.text}
        </Box>
        <HourlyIconUI
          icon={<TsunamiIcon />}
          value={`${hourData?.sigHtMt} m`}
          label="Humidity Level"
        />

        <HourlyIconUI
          icon={<FeelsLikeIcon />}
          value={`${hourData?.feelslikeC} °C`}
          label="Weather Feels Like"
        />
        <HourlyIconUI icon={<WindIcon />} value={`${hourData?.windKph} km/p`} label="Wind Speed" />
        <HourlyIconUI
          icon={<CloudCircleIcon />}
          value={`${hourData?.cloud} %`}
          label="Chances of Rain"
        />
        <HourlyIconUI
          icon={<HumidityIcon />}
          value={`${hourData?.humidity} %`}
          label="Humidity Level"
        />
      </Box>
    </>
  );
};

export const HourlyIconUI: React.FC<{
  icon: React.ReactNode;
  value: string;
  label: string;
}> = ({ icon, value, label }) => {
  return (
    <Tooltip title={label}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          mb: "7px",
        }}
      >
        <Box sx={{ lineHeight: 0.9, color: "#666" }}>{icon}</Box>
        <Box sx={{ fontSize: "14px" }}>{value}</Box>
      </Box>
    </Tooltip>
  );
};
